import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import NavBar from './NavBar';
import { getUserScanStats, PLANS, PLAN_LIMITS, updateUserSubscription, cancelUserSubscription, CES_PASS_DATES, isCESPassValid, canPurchaseCESPass } from '../services/subscriptionService';
import Icon from './Icon';

function Subscription() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [upgradeStatus, setUpgradeStatus] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const stripeCheckoutUrl = process.env.REACT_APP_STRIPE_PAYMENT_LINK || '';
  const cesPassCheckoutUrl = process.env.REACT_APP_STRIPE_CES_PASS_LINK || '';
  const hasProcessedPayment = useRef(false); // Prevent duplicate processing

  const loadStats = useCallback(async () => {
    if (!user?.uid) return;
    
    try {
      setLoading(true);
      const scanStats = await getUserScanStats(user.uid);
      setStats(scanStats);
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const handlePaymentSuccess = useCallback(async (sessionId) => {
    if (!user?.uid) {
      console.error('❌ No user ID available');
      return;
    }
    
    // Read plan parameter early, before any async operations
    const urlParams = new URLSearchParams(window.location.search);
    const planParam = urlParams.get('plan');
    const planType = planParam === 'ces_pass' ? PLANS.CES_PASS : PLANS.PRO;
    
    console.log('🚀 Starting upgrade process for user:', user.uid, 'session:', sessionId, 'plan:', planType);
    
    try {
      setUpgrading(true);
      setUpgradeStatus('Processing payment...');
      
      // Try API upgrade first
      let upgradeSuccess = false;
      let apiError = null;
      
      try {
        console.log('📡 Attempting API upgrade...');
        const response = await fetch('/api/upgrade-user', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            userId: user.uid,
            stripeSessionId: sessionId,
            plan: planParam || 'pro', // Pass plan type
          }),
        });

        console.log('📡 API response status:', response.status);
        const result = await response.json();
        console.log('📡 API response:', result);

        if (response.ok) {
          if (!result.manualUpgrade) {
            upgradeSuccess = true;
            console.log('✅ Upgrade via API successful');
          } else {
            console.log('⚠️ API returned manual upgrade flag, trying client-side...');
          }
        } else {
          apiError = result.error || 'API request failed';
          console.warn('⚠️ API upgrade failed:', apiError);
        }
      } catch (apiError) {
        console.warn('⚠️ API upgrade error:', apiError);
      }

      // If API failed, try client-side Firestore update as fallback
      if (!upgradeSuccess) {
        try {
          console.log('💾 Attempting client-side Firestore update...');
          console.log('📋 Plan type:', planType, 'User ID:', user.uid);
          setUpgradeStatus('Upgrading account...');
          
          // Calculate end date
          let endDate;
          if (planType === PLANS.CES_PASS) {
            // CES Pass ends on Jan 10, 2026
            endDate = CES_PASS_DATES.end;
            console.log('📅 CES Pass end date:', endDate);
          } else {
            // Pro subscription: 1 month from now
            endDate = new Date();
            endDate.setMonth(endDate.getMonth() + 1);
            console.log('📅 Pro subscription end date:', endDate);
          }
          
          console.log('💾 Calling updateUserSubscription with:', {
            plan: planType,
            status: 'active',
            stripeSessionId: sessionId,
            endDate: endDate,
          });
          
          await updateUserSubscription(user.uid, {
            plan: planType,
            status: 'active',
            stripeSessionId: sessionId,
            endDate: endDate,
          });
          
          upgradeSuccess = true;
          console.log('✅ Upgrade via client-side Firestore successful');
        } catch (clientError) {
          console.error('❌ Client-side upgrade failed:', clientError);
          console.error('Error details:', {
            message: clientError.message,
            code: clientError.code,
            stack: clientError.stack
          });
          
          // Provide more specific error message
          let errorMessage = `Upgrade failed: ${clientError.message || 'Unknown error'}`;
          if (clientError.code === 'permission-denied') {
            errorMessage += '\n\nThis is likely a Firestore security rules issue. Please check that users can update their own plan field.';
          }
          
          throw new Error(errorMessage);
        }
      }

      // Wait a moment for Firestore to sync, then retry verification up to 3 times
      setUpgradeStatus('Refreshing account...');
      let scanStats = null;
      let retries = 0;
      const maxRetries = 3;
      
      while (retries < maxRetries) {
        await new Promise(resolve => setTimeout(resolve, 1500)); // Wait 1.5 seconds between retries
        
        console.log(`🔄 Reloading stats... (attempt ${retries + 1}/${maxRetries})`);
        scanStats = await getUserScanStats(user.uid);
        console.log('📊 Updated stats:', scanStats);
        
        // Check if plan was updated correctly
        if (scanStats.plan === planType) {
          console.log('✅ Plan verified successfully!');
          break;
        }
        
        retries++;
        if (retries < maxRetries) {
          console.warn(`⚠️ Plan not updated yet. Expected: ${planType}, Got: ${scanStats.plan}. Retrying...`);
        }
      }
      
      setStats(scanStats);
      
      // Verify upgrade worked - be lenient: if plan is upgraded (not FREE), consider it successful
      if (scanStats.plan !== planType) {
        if (scanStats.plan === PLANS.FREE) {
          console.error('❌ Plan update failed - still on Free plan after all retries');
          console.error('❌ This could be due to:');
          console.error('   1. Firestore security rules blocking the update');
          console.error('   2. The update failing silently');
          console.error('   3. Another process overwriting the plan');
          console.error('📋 Please check:');
          console.error('   - Firestore console: users/' + user.uid);
          console.error('   - Browser console for any permission errors');
          console.error('   - Network tab for failed Firestore requests');
          throw new Error(`Plan update failed. Expected ${planType} but still on Free plan. This might be a Firestore permissions issue. Please check the browser console for details.`);
        } else {
          // Plan was upgraded but to a different plan than expected - log warning but don't fail
          console.warn(`⚠️ Plan updated to ${scanStats.plan} instead of expected ${planType}, but upgrade was successful.`);
        }
      }
      
      // Show success message
      setUpgradeStatus('success');
      const planName = scanStats.plan === PLANS.CES_PASS ? 'CES Pass' : 'Pro';
      alert(`🎉 Payment successful! Your account has been upgraded to ${planName}.`);
      
      // Remove query params from URL
      window.history.replaceState({}, '', '/subscription');
    } catch (error) {
      console.error('❌ Upgrade error:', error);
      setUpgradeStatus('error');
      alert(`Payment received but upgrade failed: ${error.message}\n\nPlease contact support with your payment ID: ${sessionId}\n\nCheck browser console (F12) for more details.`);
    } finally {
      setUpgrading(false);
    }
  }, [user]);

  useEffect(() => {
    loadStats();
    
    // Check for payment success callback
    const urlParams = new URLSearchParams(window.location.search);
    const success = urlParams.get('success');
    const sessionId = urlParams.get('session_id');
    
    console.log('🔍 Checking payment redirect:', { success, sessionId, userId: user?.uid, hasProcessed: hasProcessedPayment.current });
    
    // Only process if we have all required params and haven't processed yet
    if (success === 'true' && sessionId && user?.uid && !hasProcessedPayment.current) {
      console.log('✅ Payment redirect detected, processing upgrade...');
      hasProcessedPayment.current = true;
      handlePaymentSuccess(sessionId);
    }
  }, [user, loadStats, handlePaymentSuccess]);

  const handleUpgrade = (planType = 'pro') => {
    let checkoutUrl = '';
    
    if (planType === 'ces_pass') {
      if (!cesPassCheckoutUrl) {
        alert('Stripe payment link for CES Pass not configured. Please add REACT_APP_STRIPE_CES_PASS_LINK to your environment variables.');
        return;
      }
      checkoutUrl = cesPassCheckoutUrl;
    } else {
      if (!stripeCheckoutUrl) {
        alert('Stripe payment link not configured. Please add REACT_APP_STRIPE_PAYMENT_LINK to your environment variables.');
        return;
      }
      checkoutUrl = stripeCheckoutUrl;
    }

    // IMPORTANT: Stripe Payment Links need the success URL configured in the Stripe Dashboard
    // The success URL should be set to:
    // https://your-domain.vercel.app/subscription?success=true&session_id={CHECKOUT_SESSION_ID}&plan=pro
    // or
    // https://your-domain.vercel.app/subscription?success=true&session_id={CHECKOUT_SESSION_ID}&plan=ces_pass
    // 
    // If you haven't configured it in Stripe Dashboard, you can try appending it here,
    // but it's better to configure it in Stripe Dashboard for reliability.
    
    // Try to append success_url if not already in the link
    const successUrl = `${window.location.origin}/subscription?success=true&session_id={CHECKOUT_SESSION_ID}&plan=${planType}`;
    
    // Only append if success_url is not already in the link
    if (!checkoutUrl.includes('success_url')) {
      checkoutUrl = `${checkoutUrl}${checkoutUrl.includes('?') ? '&' : '?'}success_url=${encodeURIComponent(successUrl)}`;
    }
    
    console.log('Redirecting to Stripe checkout:', checkoutUrl);
    window.location.href = checkoutUrl;
  };

  const handleCancelSubscription = async () => {
    if (!user?.uid) return;
    
    const confirmed = window.confirm(
      'Are you sure you want to cancel your Pro subscription?\n\n' +
      'You will keep access to Pro features until the end of your current billing period.\n' +
      'After that, your account will automatically revert to the Free plan.'
    );
    
    if (!confirmed) return;
    
    try {
      setCancelling(true);
      await cancelUserSubscription(user.uid);
      await loadStats(); // Reload to show updated status
      alert('✅ Subscription cancelled successfully. You will keep Pro access until the end of your billing period.');
    } catch (error) {
      console.error('Cancel subscription error:', error);
      alert('❌ Failed to cancel subscription: ' + error.message);
    } finally {
      setCancelling(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 transition-colors duration-300">
        <NavBar title="Subscription" />
        <div className="max-w-md mx-auto px-4 py-8">
          <div className="text-center text-zinc-600 dark:text-zinc-400">Loading...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950 text-zinc-900 dark:text-zinc-100 transition-colors duration-300">
      <NavBar title="Subscription" />
      
      <main className="max-w-md mx-auto px-4 py-6 space-y-6">
        {/* Current Plan */}
        <div className="bg-zinc-50 dark:bg-zinc-900 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-800">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100">Current Plan</h2>
            <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
              stats?.isPro 
                ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white' 
                : 'bg-zinc-800 text-zinc-300'
            }`}>
              {stats?.planName || 'Free'}
            </span>
          </div>
          
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-zinc-600 dark:text-zinc-400">Scans Used</span>
              <span className="text-zinc-900 dark:text-zinc-100 font-semibold">
                {stats?.scanCount || 0} / {stats?.limit === 'Unlimited' ? '∞' : stats?.limit}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-zinc-600 dark:text-zinc-400">Remaining</span>
              <span className="text-zinc-900 dark:text-zinc-100 font-semibold">
                {stats?.remaining === 'Unlimited' ? '∞' : stats?.remaining}
              </span>
            </div>
            
            {/* Subscription End Date (for Pro users) */}
            {stats?.isPro && stats?.subscriptionEndDateFormatted && (
              <div className="pt-3 border-t border-zinc-800">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-zinc-600 dark:text-zinc-400 text-sm">Subscription Ends</span>
                  <span className="text-zinc-900 dark:text-zinc-100 font-semibold text-sm">
                    {stats.subscriptionEndDateFormatted}
                  </span>
                </div>
                {stats?.subscriptionStatus === 'cancelled' && (
                  <p className="text-yellow-400 text-xs mt-2">
                    <Icon emoji="⚠️" size="text-xs" className="mr-1" /> Cancelled - Access until end date
                  </p>
                )}
              </div>
            )}
            
            {/* Progress Bar */}
            {!stats?.isPro && (
              <div className="mt-4">
                <div className="w-full bg-zinc-200 dark:bg-zinc-800 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-blue-600 to-purple-600 h-2 rounded-full transition-all"
                    style={{
                      width: `${Math.min(100, ((stats?.scanCount || 0) / (stats?.limit || 10)) * 100)}%`
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Plan Comparison */}
        <div className="bg-zinc-50 dark:bg-zinc-900 rounded-2xl p-6 border border-zinc-200 dark:border-zinc-800">
          <h2 className="text-lg font-bold text-zinc-900 dark:text-zinc-100 mb-4">Choose Your Plan</h2>
          
          <div className="space-y-4">
            {/* Free Plan */}
            <div className={`p-4 rounded-xl border-2 ${
              !stats?.isPro 
                ? 'border-blue-600 bg-blue-900/10 dark:bg-blue-900/20' 
                : 'border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">Free</h3>
                  <p className="text-xs text-zinc-600 dark:text-zinc-400">$0</p>
                </div>
                {!stats?.isPro && (
                  <span className="text-xs text-blue-400 font-medium">Current</span>
                )}
              </div>
              <div className="space-y-2 text-sm text-zinc-600 dark:text-zinc-400">
                <div className="flex items-center gap-2">
                  <Icon emoji="✓" size="text-sm" />
                  <span>10 business card scans</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon emoji="✓" size="text-sm" />
                  <span>AI-powered extraction</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon emoji="✓" size="text-sm" />
                  <span>Follow-up email drafts</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon emoji="✓" size="text-sm" />
                  <span>Lead management</span>
                </div>
              </div>
            </div>

            {/* Pro Plan */}
            <div className={`p-4 rounded-xl border-2 ${
              stats?.isPro 
                ? 'border-purple-600 bg-purple-900/10 dark:bg-purple-900/20' 
                : 'border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50'
            }`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">Pro</h3>
                  <p className="text-xs text-zinc-600 dark:text-zinc-400">$8/month • Unlimited scans</p>
                </div>
                {stats?.isPro && (
                  <span className="text-xs text-purple-400 font-medium">Active</span>
                )}
              </div>
              <div className="space-y-2 text-sm text-zinc-600 dark:text-zinc-400 mb-4">
                <div className="flex items-center gap-2">
                  <Icon emoji="✓" size="text-sm" />
                  <span className="text-zinc-900 dark:text-zinc-100 font-medium">Unlimited business card scans</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon emoji="✓" size="text-sm" />
                  <span>AI-powered extraction</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon emoji="✓" size="text-sm" />
                  <span>Follow-up email drafts</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon emoji="✓" size="text-sm" />
                  <span>Lead management</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon emoji="✓" size="text-sm" />
                  <span>Priority support</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon emoji="✓" size="text-sm" />
                  <span>Auto send follow up email <span className="text-xs text-zinc-500 dark:text-zinc-500">(coming soon)</span></span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon emoji="✓" size="text-sm" />
                  <span>Batch upload <span className="text-xs text-zinc-500 dark:text-zinc-500">(coming soon)</span></span>
                </div>
              </div>
              
              {!stats?.isPro && (
                <button
                  onClick={() => handleUpgrade('pro')}
                  className="w-full py-3 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white rounded-lg font-semibold transition-all"
                >
                  Upgrade to Pro
                </button>
              )}
              
              {stats?.isPro && stats?.subscriptionStatus !== 'cancelled' && !stats?.isCESPass && (
                <button
                  onClick={handleCancelSubscription}
                  disabled={cancelling}
                  className="w-full py-2.5 bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 text-zinc-900 dark:text-zinc-300 rounded-lg font-medium transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                >
                  {cancelling ? 'Cancelling...' : 'Cancel Subscription'}
                </button>
              )}
              
              {stats?.isPro && stats?.subscriptionStatus === 'cancelled' && (
                <div className="w-full py-2.5 bg-yellow-900/20 border border-yellow-600/50 text-yellow-300 rounded-lg text-center text-sm font-medium">
                  Subscription Cancelled
                </div>
              )}
            </div>

            {/* CES Pass Plan */}
            {canPurchaseCESPass() && (
              <div className={`p-4 rounded-xl border-2 ${
                stats?.isCESPass 
                  ? 'border-orange-600 bg-orange-900/10 dark:bg-orange-900/20' 
                  : 'border-zinc-200 dark:border-zinc-800 bg-zinc-50 dark:bg-zinc-800/50'
              }`}>
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <h3 className="font-semibold text-zinc-900 dark:text-zinc-100">CES Pass</h3>
                    <p className="text-xs text-zinc-600 dark:text-zinc-400">$12 one-time • Jan 5 - Jan 10, 2026</p>
                  </div>
                  {stats?.isCESPass && (
                    <span className="text-xs text-orange-400 font-medium">Active</span>
                  )}
                </div>
                <div className="space-y-2 text-sm text-zinc-600 dark:text-zinc-400 mb-4">
                  <div className="flex items-center gap-2">
                    <Icon emoji="✓" size="text-sm" />
                    <span className="text-zinc-900 dark:text-zinc-100 font-medium">Unlimited scans during CES</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon emoji="✓" size="text-sm" />
                    <span>AI-powered extraction</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon emoji="✓" size="text-sm" />
                    <span>Follow-up email drafts</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon emoji="✓" size="text-sm" />
                    <span>Lead management</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon emoji="✓" size="text-sm" />
                    <span>Valid: Jan 5 - Jan 10, 2026</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Icon emoji="✓" size="text-sm" />
                    <span>CES insight newsletter</span>
                  </div>
                </div>
                
                {!stats?.isCESPass && (
                  <button
                    onClick={() => handleUpgrade('ces_pass')}
                    className="w-full py-3 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-white rounded-lg font-semibold transition-all"
                  >
                    Get CES Pass
                  </button>
                )}
                
                {stats?.isCESPass && (
                  <div className="w-full py-2.5 bg-orange-900/20 border border-orange-600/50 text-orange-300 rounded-lg text-center text-sm font-medium">
                    Active until Jan 10, 2026
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Upgrade Status */}
        {upgrading && upgradeStatus && (
          <div className={`rounded-xl p-4 text-sm ${
            upgradeStatus === 'success' 
              ? 'bg-green-900/20 border border-green-600' 
              : upgradeStatus === 'error'
              ? 'bg-red-900/20 border border-red-600'
              : 'bg-blue-900/20 border border-blue-600'
          }`}>
            <p className={`font-medium ${
              upgradeStatus === 'success' 
                ? 'text-green-300' 
                : upgradeStatus === 'error'
                ? 'text-red-300'
                : 'text-blue-300'
            }`}>
              {upgradeStatus === 'success' ? '✅ ' : upgradeStatus === 'error' ? '❌ ' : ''}
              {upgradeStatus}
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

export default Subscription;

